import { Documents } from "@/components/documents";
import { useCalculation } from "@/hooks/use-calculation";
import { ContentBox } from "@msuite/ui/content-box";
import { ContentCard } from "@msuite/ui/content-card";
import { icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { Tabs } from "@msuite/ui/tabs";
import { Tag } from "@msuite/ui/tag";
import { getCalculationStateColor } from "@msuite/utils/calculations";
import { guardString } from "@msuite/utils/strings";
import { useParams } from "react-router";
import { CalculationLockedAlert } from "../calculation-locked-alert";
import { CalculationGeneral } from "./calculation-general";
import { CalculationMembers } from "./calculation-members";
import { CalculationMenu } from "./calculation-menu";
import { CalculationParsc } from "./calculation-parsc";
import { CalculationServices } from "./calculation-services";
import { calculationTabCyId } from "./tabs";

export function Calculation() {
	/** Hooks */
	const { t } = useTranslation();
	const params = useParams();
	const { calculation } = useCalculation(params.calculationId ?? "");
	const calculationId = params.calculationId ?? "";

	/** Guard */
	if (!calculationId) return null;

	/** Render */
	return (
		<ContentBox>
			<ContentCard
				title={t("calculations.calculation")}
				icon={icon("Euro")}
				identifier={calculation ? calculationId : undefined}
				dataPageBreadcrumbsTitle={calculation?.name ?? undefined}
			>
				<div className="h-stack">
					<p>{guardString(calculation?.name)}</p>
					{calculation?.state === "locked" && (
						<Tag
							color={getCalculationStateColor("locked")}
							variant="plain"
						>
							{t("calculations.locked")}
						</Tag>
					)}
				</div>
			</ContentCard>
			{calculation && <CalculationLockedAlert calculation={calculation} />}
			<Tabs
				panelKey="calculation-tabs"
				withHeader
				header={() => <CalculationMenu calculationId={calculationId} />}
				groups={[
					{
						name: "general",
						panels: [
							{
								label: "General",
								panel: () => <CalculationGeneral calculationId={calculationId} />,
								errors: [],
								cyId: calculationTabCyId("general"),
							},
						],
					},
					{
						name: "details",
						panels: [
							{
								label: t("organization.data.services"),
								panel: () => <CalculationServices calculationId={calculationId} />,
								errors: [],
								cyId: calculationTabCyId("services"),
							},
							calculation?.type === "parsc"
								? {
										label: "Parsc",
										panel: () => <CalculationParsc calculationId={calculationId} />,
										errors: [],
										cyId: calculationTabCyId("parsc"),
									}
								: null,
							{
								label: t("files.files"),
								panel: () => (
									<Documents
										documentFilesTable="serviceCalculationFiles"
										identifier={calculationId}
										parent={
											calculation ? { subject: "Calculation", resource: calculation } : undefined
										}
										isReadOnly={calculation?.state === "locked"}
									/>
								),
								errors: [],
								cyId: calculationTabCyId("files"),
							},
							{
								label: t("organization.members"),
								panel: () => <CalculationMembers calculationId={calculationId} />,
								errors: [],
								cyId: calculationTabCyId("members"),
							},
						],
					},
				]}
			/>
		</ContentBox>
	);
}
